import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Drumstick, RefreshCw, Zap, ArrowRight, Sparkles, ChefHat } from 'lucide-react';
export function MethodSection() {
  const steps = [{
    number: '01',
    icon: <Drumstick className="w-6 h-6" data-id="element-140" />,
    title: 'Proteína Base',
    desc: <>
          Cocinas o compras <strong data-id="element-141">una sola proteína</strong> el domingo.
        </>,
    example: 'Ej: Pollo desmenuzado 🍗',
    accent: 'from-naranja-500 to-mostaza-500',
    iconBg: 'bg-naranja-500',
    borderColor: 'border-naranja-300',
    shadow: 'shadow-[0_6px_24px_-4px_rgba(251,133,0,0.3)]'
  }, {
    number: '02',
    icon: <RefreshCw className="w-6 h-6" data-id="element-142" />,
    title: 'Transformación Rápida',
    desc: <>
          Ese mismo pollo se convierte en <strong data-id="element-143">3 loncheras distintas</strong>{' '}
          durante la semana.
        </>,
    example: 'Wrap → Pasta → Bento Box',
    accent: 'from-azul-polvo-500 to-sage-500',
    iconBg: 'bg-azul-polvo-500',
    borderColor: 'border-azul-polvo-200',
    shadow: 'shadow-[0_6px_24px_-4px_rgba(142,202,230,0.4)]'
  }, {
    number: '03',
    icon: <Zap className="w-6 h-6" data-id="element-144" />,
    title: 'La Regla 1-1-1',
    desc: <>
          <strong className="text-lg" data-id="element-145">1 Proteína + 1 Fibra + 1 Energía</strong>
        </>,
    example: '= Lonchera perfecta ¡Sin pensar!',
    accent: 'from-sage-500 to-verde-manzana-500',
    iconBg: 'bg-sage-500',
    borderColor: 'border-sage-300',
    shadow: 'shadow-[0_6px_24px_-4px_rgba(122,175,135,0.35)]'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.18
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 180,
        damping: 18
      }
    }
  };
  return <section className="pt-0 pb-16 px-4 bg-azul-naval-500 text-white relative overflow-hidden" data-id="element-146">
      {/* Before/After image */}
      <div className="max-w-3xl mx-auto mb-10" data-id="element-147">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-148">
          <img src="https://cdn.magicpatterns.com/uploads/1nmxkUrPxU6a2g61yom9Yx/15.png" alt="Antes y después - De mañanas caóticas a loncheras organizadas" className="w-full h-auto object-cover" data-id="element-149" />
        </motion.div>
      </div>

      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-azul-polvo-500 rounded-full mix-blend-overlay filter blur-[100px] opacity-20" data-id="element-150"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-naranja-500 rounded-full mix-blend-overlay filter blur-[120px] opacity-15" data-id="element-151"></div>
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '28px 28px'
    }} data-id="element-152"></div>

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-153">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-154">
          <motion.span initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          type: 'spring'
        }} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-bold px-5 py-2 rounded-full text-xs mb-5 uppercase tracking-wider border border-white/20" data-id="element-155">
            <ChefHat className="w-4 h-4" data-id="element-156" />
            El Mecanismo Único
            <Sparkles className="w-4 h-4" data-id="element-157" />
          </motion.span>
          <h2 className="text-3xl md:text-4xl font-black mb-4 leading-tight" data-id="element-158">
            Te presento el Método de{' '}
            <span className="text-naranja-300" data-id="element-159">"Loncheras Saludables"</span>
          </h2>
          <p className="text-base md:text-lg text-azul-polvo-200 max-w-2xl mx-auto font-medium leading-relaxed" data-id="element-160">
            No es un recetario más de redes. Es la solución para que{' '}
            <strong className="text-white" data-id="element-161">nunca más</strong> te quedes sin
            saber qué lonchera preparar — saludable, rica, y que{' '}
            <strong className="text-white" data-id="element-162">le encante a tu hijo</strong>.
          </p>
        </motion.div>

        {/* Steps */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-163">
          {steps.map((step, idx) => <motion.div key={idx} variants={cardVariants} whileHover={{
          y: -4
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} className={`bg-white text-charcoal-900 p-6 rounded-2xl border ${step.borderColor} ${step.shadow} relative overflow-hidden`} data-id="element-164">
              {/* Gradient top stripe */}
              <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${step.accent}`} data-id="element-165"></div>

              <div className="flex items-center gap-3 mb-4 mt-1" data-id="element-166">
                <div className={`w-10 h-10 ${step.iconBg} text-white rounded-xl flex items-center justify-center shadow-md`} data-id="element-167">
                  {step.icon}
                </div>
                <span className="text-xs font-black text-charcoal-400 uppercase tracking-widest" data-id="element-168">
                  Paso {step.number}
                </span>
              </div>

              <h3 className="text-lg font-black text-charcoal-900 mb-2" data-id="element-169">
                {step.title}
              </h3>
              <p className="text-charcoal-700 text-sm leading-relaxed mb-3" data-id="element-170">
                {step.desc}
              </p>
              <div className="bg-cream-50 px-3 py-2 rounded-lg" data-id="element-171">
                <p className="text-xs font-bold text-charcoal-600 flex items-center gap-1.5" data-id="element-172">
                  <ArrowRight className="w-3.5 h-3.5 text-naranja-500" data-id="element-173" />
                  {step.example}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom connector phrase */}
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="text-center mt-8 text-azul-polvo-200 text-sm font-medium" data-id="element-174">
          ✨ Así de simple:{' '}
          <strong className="text-white" data-id="element-175">
            1 proteína → 5 loncheras diferentes
          </strong>{' '}
          en menos de 10 minutos cada una.
        </motion.p>
      </div>
    </section>;
}